import {createSelector} from 'reselect'
import * as EntitiesSelectors from '../../entities/selectors'
import {getCustomers} from '../../entities/selectors'
import {
    getEditableCustomerIds,
    getEditableCustomerIdsWithExcludeSelf,
    getEditableProjectIds,
    getEditableProjectIdsWithExcludeSelf,
    getSelectedTaskIds,
    getTaskStatusFilter,
    getViewMode,
    isSingleProjectSelected
} from '../common/selectors'
import {TaskManagementViewMode} from '../constants'
import {calcProjectsWithGroupedTasks, filterVisibleProjects, isProjectFullyLoaded} from './utils'
import {workflowStatusComparatorWithStatusType} from "../../workflowStatus/utils"
import {createTokensHighlighterForSearch} from "../../util/TokensHighlighter"
import {arrayToSet} from '../../atUtils'
import _ from 'underscore'
import {isCompletedTypeSelected, isOnlyDirectlySelectedStatuses, isOpenTypeSelected} from '../../workflowStatus/components/statusFilter/utils'

const getKanbanState = state => state.taskManagement.kanban

export const getWorkflowStatusIds = state => getKanbanState(state).workflowStatusIds

export const getTaskCountsByWorkflowStatus = state => getKanbanState(state).taskCountsByWorkflowStatus

export const getProjectIds = state => getKanbanState(state).projectIds

export const getProjectLoadingPositions = state => getKanbanState(state).projectLoadingPositions

export const isLoadingInitialData = state => getKanbanState(state).loadingInitialData

export const getLoadingProjectId = state => getKanbanState(state).loadingProjectId

export const isLoadingTasks = state => getLoadingProjectId(state) !== null

export const isLoaded = state => getKanbanState(state).isLoaded

export const getTaskIdsOrder = state => getKanbanState(state).taskIdsOrder

export const isMinimizedCards = state => getKanbanState(state).minimizedCards

export const isNeedToReloadCounters = state => getKanbanState(state).needToLoadCounters

export const getCollapsedProjects = createSelector(
    getKanbanState,
    (kanbanState) => kanbanState.collapsedProjects
)

export const getCollapsedProjectsWithIdsSet = createSelector(
    getCollapsedProjects,
    (collapsedProjects) => {
        return {
            all: collapsedProjects.all,
            idsSet: arrayToSet(collapsedProjects.ids),
        }
    }
)

export const isNeedToReloadKanban = createSelector(
    getViewMode,
    isLoaded,
    (viewMode, isLoaded) => {
        return viewMode === TaskManagementViewMode.KANBAN && isLoaded === false
    }
)

export const getUpdatingTaskIds = state => getKanbanState(state).updatingTaskIds

export const getLoadedTasksNameFilter = state => getKanbanState(state).loadedTasksNameFilter

// use only for checking empty project and not send request to server
export const getProjectIdsContainingTasks = state => getKanbanState(state).projectIdsContainingTasks

export const getTasks = createSelector(
    EntitiesSelectors.getTasks,
    getTaskIdsOrder,
    (allTasks, taskIds) => taskIds.map(taskId => allTasks[taskId])
)

export const getProjects = createSelector(
    EntitiesSelectors.getProjects,
    getProjectIds,
    (allProjects, projectIds) => projectIds.map(projectId => allProjects[projectId])
)

export const getWorkflowStatuses = createSelector(
    EntitiesSelectors.getWorkflowStatuses,
    getWorkflowStatusIds,
    getTaskCountsByWorkflowStatus,
    (allStatuses, kanbanWorkflowStatusIds, kanbanTaskCounts) => {
        const statuses = kanbanWorkflowStatusIds.map(
            id => ({...allStatuses[id], count: kanbanTaskCounts[id]})
        )
        statuses.sort(workflowStatusComparatorWithStatusType)
        return statuses
    }
)

export const isLoading = createSelector(
    isLoadingInitialData,
    isLoadingTasks,
    (loadingInitial, loadingTasks) => loadingInitial || loadingTasks
)

/**
 * Detects that search enable for loaded kanban data
 */
export const isSearchEnable = createSelector(
    getLoadedTasksNameFilter,
    (loadedTasksNameFilter) => !_.isEmpty(loadedTasksNameFilter)
)

/**
 * Predicate that checks is possible to see project on kanban
 */
export const getVisibleProjectPredicate = createSelector(
    isSearchEnable,
    getProjectIdsContainingTasks,
    EntitiesSelectors.getProjects,
    getTaskStatusFilter,
    (searchEnable, projectWithTasksIds, projectsMap, taskStatusFilter) => (projectId) => {
        // always show project with tasks
        if (!!projectWithTasksIds[projectId]) {
            return true
        }

        // search enable -> hide all empty
        if (searchEnable) {
            return false
        }

        // if directly statuses -> hide all empty
        if (isOnlyDirectlySelectedStatuses(taskStatusFilter)) {
            return false
        }

        // if open group selected -> hide archived
        if (isOpenTypeSelected(taskStatusFilter) && !isCompletedTypeSelected(taskStatusFilter)) {
            return !projectsMap[projectId].archived
        }

        return true
    }
)

/**
 * Detect that projects should always expanded
 */
export const doesIgnoreCollapse = createSelector(
    isSingleProjectSelected,
    isSearchEnable,
    (isSingleProjectSelected, isSearchEnable) => isSingleProjectSelected || isSearchEnable
)

/**
 * Set of ids that collapsed. Consider `all` option
 */
export const getCollapsedProjectsIdsSet = createSelector(
    getCollapsedProjects,
    getProjectIds,
    (collapsedProjects, projectIds) => {
        return arrayToSet(collapsedProjects.all === true ? projectIds : collapsedProjects.ids)
    }
)

/**
 * Get predicate which checks that project is collapsed or not on page
 */
export const getProjectCollapsedPredicate = createSelector(
    doesIgnoreCollapse,
    getCollapsedProjectsIdsSet,
    (ignoreCollapse, collapsedProjectIds) => (projectId) => !ignoreCollapse && !!collapsedProjectIds[projectId]
)

/**
 * Get predicate which checks that for project need to load data
 */
export const getLoadableProjectPredicate = createSelector(
    getProjectCollapsedPredicate,
    getVisibleProjectPredicate,
    (isProjectCollapsed, isProjectVisiblePredicate) => (projectId) => {
        return !isProjectCollapsed(projectId) && isProjectVisiblePredicate(projectId)
    }
)

/**
 * Checks that all loadable projects are loaded
 */
export const areProjectsFullyLoaded = createSelector(
    getProjectIds,
    getProjectLoadingPositions,
    getLoadableProjectPredicate,
    (projectIds, loadingPositions, isProjectLoadable) => projectIds.every(
        id => !isProjectLoadable(id) || isProjectFullyLoaded(id, loadingPositions)
    )
)

export const getLoadedTasksNameTokensHighlighter = createSelector(
    getLoadedTasksNameFilter,
    (nameFilter) => createTokensHighlighterForSearch(nameFilter)
)

export const getTasksWithHighlightedNames = createSelector(
    getTasks,
    getLoadedTasksNameTokensHighlighter,
    (tasks, tokensHighlighter) => tasks.map(task => ({
        ...task,
        highlightedName: tokensHighlighter ? tokensHighlighter.highlightTokens(task.name) : null
    }))
)

/**
 * Get all projects with grouped tasks. For internal usage.
 */
const getAllProjectsWithGroupedTasks = createSelector(
    getProjects,
    getCustomers,
    getTasksWithHighlightedNames,
    getEditableProjectIds,
    getEditableProjectIdsWithExcludeSelf,
    getEditableCustomerIds,
    getEditableCustomerIdsWithExcludeSelf,
    (projects, customers, tasks, editableProjectIds, editableProjectIdsWithExcludeSelf, editableCustomerIds, editableCustomerIdsWithExcludeSelf) => {
        return calcProjectsWithGroupedTasks(
            projects,
            customers,
            tasks,
            editableProjectIds,
            editableProjectIdsWithExcludeSelf,
            editableCustomerIds,
            editableCustomerIdsWithExcludeSelf,
        )
    }
)

/**
 * Get projects with tasks that will be rendered on page
 */
export const getProjectsWithGroupedTasks = createSelector(
    getAllProjectsWithGroupedTasks,
    getProjectCollapsedPredicate,
    getProjectLoadingPositions,
    getVisibleProjectPredicate,
    (allProjects, isProjectCollapsedPredicate, projectLoadingPositions, isProjectVisiblePredicate) =>
        filterVisibleProjects(allProjects, isProjectCollapsedPredicate, projectLoadingPositions, isProjectVisiblePredicate)
)

/**
 * Detect that no tasks in current filters.
 * The only way to detect it is task's counters now.
* */
export const thereAreNoTasks = createSelector(
    getTaskCountsByWorkflowStatus,
    isLoading,
    (counters, isLoading) => Object.values(counters).every(count => count === 0) && !isLoading
)

/**
 * Select list of ids that currently visible on kanban
 */
export const getVisibleTaskIds = createSelector(
    getTaskIdsOrder,
    getWorkflowStatusIds,
    EntitiesSelectors.getTasks,
    (taskIds, workflowStatusIds, tasks) => {
        const wfsIdsSet = arrayToSet(workflowStatusIds)
        return taskIds.filter((id) => {
            return !!tasks[id] && !!wfsIdsSet[tasks[id].workflowStatusId]
        })
    }
)



// WEBPACK FOOTER //
// ./web/js/taskManagement/kanban/selectors.js